<template>
    <modal :show="true" :closable="!loading" @close="stopEditing" :custom_modal_class="'modal-lg fullwidth-modal'">
        <div slot="header">
            <div class="modal-header">
                <h5 class="modal-title">{{ $t('files.edit_photo') }}</h5>
                <button type="button" class="close" @click="stopEditing" aria-label="Close">
                    <span aria-hidden="true">×</span>
                </button>
            </div>
        </div>

        <div slot="body">
            <div class="form-group ">
                <label class="form-control-label">{{ $t('files.name') }}</label>
                <input type="text" class="form-control" v-model="requestData.name" :disabled="loading">
            </div>

            <div class="cropper-wrapper">
                <div class="cropper-loading" v-if="loading"/>
                <vue-cropper
                    class="cropper"
                    ref="cropper"
                    :src="src"
                    :view-mode="0"
                    @crop="updateCropData"
                    @zoom="updateCropData"
                    @ready="cropperInit"
                />
            </div>

            <div class="alert alert-info mt-2 d-none d-sm-block">
                {{ $t('files.scroll_info') }}
            </div>
        </div>

        <div slot="footer" class="cropper-footer">
            <div class="cropper-actions">
                <button @click="rotate('left')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.rotate_left')">
                    <icon name="undo"></icon>
                </button>
                <button @click="rotate('right')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.rotate_right')">
                    <icon name="redo"></icon>
                </button>
                <button @click="scale('vertical')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.flip_vertical')" style="padding: 0.5rem 1rem;">
                    <icon name="arrows-alt-v"></icon>
                </button>
                <button @click="scale('horizontal')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.flip_horizontal')">
                    <icon name="arrows-alt-h"></icon>
                </button>
                <button @click="zoom('in')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.zoom_in')">
                    <icon name="search-plus"></icon>
                </button>
                <button @click="zoom('out')" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.zoom_out')">
                    <icon name="search-minus"></icon>
                </button>
                <button @click="setAspectRation()" class="btn btn-sm btn-secondary" :disabled="loading" v-tooltip="$t('files.toggle_aspect_ratio')">
                    <icon v-if="!lockAspectRatio" name="link"></icon>
                    <icon v-else name="unlink"></icon>
                </button>
            </div>

            <button type="button" class="btn btn-success btn-icon btn-sm" @click="save()" :disabled="loading" style="margin-left: auto;">
                {{ $t('files.save') }}
            </button>
            <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="stopEditing" :disabled="loading">
                {{ $t('files.close') }}
            </button>
        </div>

    </modal>
</template>

<script>

    import Modal from "./Modal";
    import VueCropper from 'vue-cropperjs';
    import 'cropperjs/dist/cropper.css';

    export default {

        components: {
            Modal,
            VueCropper
        },

        props: {
            ceremonyCode: {
                type: String,
                required: true
            },

            file: {
                type: Object,
                required: true
            },

            blockId: {
                type: Number,
                default: null
            }
        },

        computed: {
            src() {
                return this.file.original_path.replace('/:code/', '/' + this.ceremonyCode + '/');
            }
        },

        watch: {
            file() {
                this.requestData.name = this.file.name;
                this.$refs.cropper.setData(this.file.crop_data);
            },
            blockId() {
                this.requestData.block_id = this.file.blockId;
            }
        },

        data() {
            return {
                initialEvent: false,
                loading: false,

                requestData: {
                    name: this.file.name,
                    cropping: null,
                    block_id: this.blockId
                },

                lockAspectRatio: false,
            }
        },

        methods: {
            rotate(direction) {
                this.$refs.cropper.rotate(direction === 'left' ? -90 : 90);
                this.$refs.cropper.moveTo(0, 0);
            },

            scale(direction) {
                const data = this.$refs.cropper.getData();

                if(!data){
                    return;
                }

                if (direction === 'vertical') {
                    this.$refs.cropper.scaleY(-data.scaleY);
                }

                if (direction === 'horizontal') {
                    this.$refs.cropper.scaleX(-data.scaleX);
                }
            },

            zoom(direction){
                this.$refs.cropper.relativeZoom(direction === 'in' ? 0.1 : -0.1)
            },

            cropperInit() {
                this.$refs.cropper.setData(this.file.crop_data);
            },

            stopEditing() {
                this.$emit('close');
            },

            updateCropData(event) {
                if (!this.initialEvent) {
                    this.initialEvent = true;
                    return;
                }

                this.requestData.cropping = event.detail;
            },

            setAspectRation() {
                if(this.lockAspectRatio) {
                    this.lockAspectRatio = false;
                    this.$refs.cropper.setAspectRatio(NaN);
                } else {
                    this.lockAspectRatio = true;
                    this.$refs.cropper.setAspectRatio(16/9);
                }
            },

            async save() {
                this.loading = true;

                try {
                    const response = await this.$http.put(`/api/ceremonies/code/${this.ceremonyCode}/files/${this.file.id}`, this.requestData);

                    this.$emit('save', response.data)
                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save edited file');
                }

                this.loading = false;
            }
        }

    }

</script>

<style lang="scss" scoped>

    .cropper {
        max-height: 40vh;
        max-width: 1200px;

        @media(max-width: 1300px) {
            max-width: 80vw;
        }

        @media(max-width: 767px) {
            max-width: none;
        }
    }

    .cropper-wrapper{
        position: relative;

        .cropper-loading {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 100;
        }
    }

    .cropper-actions {
        button {
            padding: 0.5rem 0.75rem;
        }
    }

    .cropper-footer {
        display: flex;
        width: 100%;

        @media(max-width: 767px) {
            display: block;
            text-align: right;

            .cropper-actions {
                margin-bottom: 15px;
                text-align: center;
            }
        }
    }
</style>
